import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";

const fields = [
    { key: "name", label: "Name", type: "text", placeholder: "Your full name" },
    { key: "role", label: "Role", type: "text", placeholder: "e.g. CHRO, Head of People" },
    { key: "company", label: "Company", type: "text", placeholder: "Organisation" },
    { key: "city", label: "City", type: "text", placeholder: "Mumbai" },
    { key: "phone", label: "Phone", type: "tel", placeholder: "+91" },
    { key: "email", label: "Email", type: "email", placeholder: "you@company.com" },
];

export default function PilotForm({ invitation, whatsappAlt, whatsappNumber }) {
    const [form, setForm] = useState({
        name: "",
        role: "",
        company: "",
        city: "",
        phone: "",
        email: "",
        note: "",
    });
    const [status, setStatus] = useState({ state: "idle", error: "" });

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ state: "submitting", error: "" });
        try {
            await api.post("/enquiries", form);
            setStatus({ state: "done", error: "" });
            setForm({ name: "", role: "", company: "", city: "", phone: "", email: "", note: "" });
        } catch (err) {
            setStatus({
                state: "error",
                error: formatApiError(err.response?.data?.detail) || err.message,
            });
        }
    };

    if (status.state === "done") {
        return (
            <div
                data-testid="pilot-form-success"
                className="border border-[hsl(var(--brass)/0.5)] bg-[hsl(var(--cream))] p-12 md:p-16 text-center"
            >
                <p className="eyebrow">Received</p>
                <p className="font-serif text-3xl md:text-4xl mt-4 leading-snug max-w-2xl mx-auto">
                    Thank you. A senior member of our team will be in touch within one working day.
                </p>
                <p className="mt-6 text-[14px] text-[hsl(var(--muted))]">
                    Until then, ask Mira anything you would normally ask your assistant.
                </p>
            </div>
        );
    }

    return (
        <div
            className="border border-[hsl(var(--brass)/0.5)] bg-[hsl(var(--cream))] p-8 md:p-14"
            data-testid="pilot-form-card"
        >
            <p className="font-serif text-2xl md:text-[32px] leading-snug max-w-2xl">
                {invitation}
            </p>
            <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-8" data-testid="pilot-form">
                {fields.map((f) => (
                    <div className="field" key={f.key}>
                        <label htmlFor={`pilot-${f.key}`}>{f.label}</label>
                        <input
                            id={`pilot-${f.key}`}
                            type={f.type}
                            placeholder={f.placeholder}
                            required
                            value={form[f.key]}
                            onChange={onChange(f.key)}
                            data-testid={`pilot-${f.key}-input`}
                        />
                    </div>
                ))}
                <div className="field md:col-span-2">
                    <label htmlFor="pilot-note">One line about what you need</label>
                    <textarea
                        id="pilot-note"
                        rows={2}
                        placeholder="Eg. employee concierge for our Mumbai HQ"
                        value={form.note}
                        onChange={onChange("note")}
                        data-testid="pilot-note-input"
                    />
                </div>

                {status.state === "error" && (
                    <p className="md:col-span-2 text-[13px] text-[hsl(var(--destructive))]" data-testid="pilot-form-error">
                        {status.error}
                    </p>
                )}

                <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4">
                    <button
                        type="submit"
                        disabled={status.state === "submitting"}
                        className="btn-primary disabled:opacity-60"
                        data-testid="pilot-submit-button"
                    >
                        {status.state === "submitting" ? "Sending…" : "Request a private pilot"}
                    </button>
                    <a
                        href={`https://wa.me/${whatsappNumber}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[13px] tracking-[0.06em] text-[hsl(var(--muted))] hover:text-[hsl(var(--brass))] transition-colors"
                        data-testid="pilot-whatsapp-link"
                    >
                        {whatsappAlt}
                    </a>
                </div>
            </form>
        </div>
    );
}
